import { BaseService } from '../base/BaseService';

export type CreditLedgerKind = 'billable' | 'included' | 'adjustment';

export interface CreditWallet {
  planCreditsRemaining: number;
  additionalCreditsRemaining: number;
  creditsAvailable: number;
  planAllocation: number;
  cycleEndsAt: string;
  planTier: string;
}

export interface CreditLedgerItem {
  occurredAt: string;
  category: string;
  activityName: string;
  actorDisplayName: string;
  amount: number;
  kind: CreditLedgerKind;
}

export interface CreditLedgerPage {
  items: CreditLedgerItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface CreditDailyUsage {
  day: string;
  credits: number;
}

export interface CreditLedgerFilters {
  cursor?: string | null;
  kind?: CreditLedgerKind | null;
}

export interface CreditReceiptItem {
  category: string;
  activityName: string;
  creditsCharged: number;
  quantity: number;
  occurredAt: string;
}

/**
 * `requestId` is only a local correlation key for matching an already-loaded
 * chat turn to its authoritative server receipt. It is not presentation data.
 */
export interface CreditReceipt {
  requestId: string;
  creditsCharged: number;
  quantity: number;
  activity: string;
  items: CreditReceiptItem[];
}

type RpcError = { message: string } | null;
type UntypedRpcClient = {
  rpc: (
    name: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: RpcError }>;
};

type WalletRpcResult = {
  plan_credits_remaining: unknown;
  additional_credits_remaining: unknown;
  credits_available: unknown;
  plan_credits_allocation: unknown;
  cycle_ends_at: unknown;
  plan_tier: unknown;
};

type LedgerRpcRow = {
  entry_type: unknown;
  quantity?: unknown;
  credits_delta: unknown;
  category: unknown;
  activity_name: unknown;
  occurred_at: unknown;
  request_id?: unknown;
};

type SafeLedgerRpcRow = {
  occurred_at: unknown;
  category: unknown;
  activity_name: unknown;
  actor_display_name: unknown;
  amount: unknown;
  kind: unknown;
};

type LedgerPageRpcResult = {
  items: unknown;
  next_cursor: unknown;
  has_more: unknown;
};

const LEDGER_PAGE_SIZE = 50;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function numberValue(value: unknown, field: string): number {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) throw new Error(`Credit wallet RPC returned invalid ${field}`);
  return number;
}

function stringValue(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Credit wallet RPC returned invalid ${field}`);
  }
  return value;
}

function ledgerKind(value: unknown): CreditLedgerKind {
  if (value === 'billable' || value === 'included' || value === 'adjustment') return value;
  throw new Error('Credit ledger RPC returned invalid item kind');
}

function mapLedgerRow(row: SafeLedgerRpcRow): CreditLedgerItem {
  return {
    occurredAt: stringValue(row.occurred_at, 'occurred_at'),
    category: stringValue(row.category, 'category'),
    activityName: stringValue(row.activity_name, 'activity_name'),
    actorDisplayName: stringValue(row.actor_display_name, 'actor_display_name'),
    amount: numberValue(row.amount, 'amount'),
    kind: ledgerKind(row.kind),
  };
}

function mapLedgerPage(result: unknown): CreditLedgerPage {
  const page = asRecord(result) as LedgerPageRpcResult | null;
  if (!page || !Array.isArray(page.items)) {
    throw new Error('Credit ledger RPC returned an invalid page');
  }
  if (typeof page.has_more !== 'boolean') {
    throw new Error('Credit ledger RPC returned invalid has_more');
  }
  if (page.next_cursor !== null && (typeof page.next_cursor !== 'string' || !page.next_cursor)) {
    throw new Error('Credit ledger RPC returned invalid next_cursor');
  }
  if (page.has_more !== (page.next_cursor !== null)) {
    throw new Error('Credit ledger RPC returned inconsistent pagination state');
  }

  return {
    items: page.items.map((item, index) => {
      const row = asRecord(item);
      if (!row) throw new Error(`Credit ledger RPC returned invalid item at index ${index}`);
      return mapLedgerRow(row as SafeLedgerRpcRow);
    }),
    nextCursor: page.next_cursor as string | null,
    hasMore: page.has_more,
  };
}

/** Browser read facade for the owner-scoped, immutable credit wallet. */
export class CreditService extends BaseService {
  private async rpc<T>(name: string, args?: Record<string, unknown>): Promise<T> {
    // Generated database types are intentionally refreshed by their owning task.
    // Keep this temporary boundary narrow rather than casting the whole client.
    const client = this.client as unknown as UntypedRpcClient;
    const { data, error } =
      args === undefined ? await client.rpc(name) : await client.rpc(name, args);
    if (error) throw error;
    return data as T;
  }

  async getWallet(): Promise<CreditWallet | null> {
    await this.ensureAuthenticatedOwner();
    const result = await this.rpc<unknown>('get_credit_wallet');
    if (result === null) return null;
    const wallet = asRecord(result) as WalletRpcResult | null;
    if (!wallet) throw new Error('Credit wallet RPC returned an invalid wallet');

    return {
      planCreditsRemaining: numberValue(wallet.plan_credits_remaining, 'plan_credits_remaining'),
      additionalCreditsRemaining: numberValue(
        wallet.additional_credits_remaining,
        'additional_credits_remaining',
      ),
      creditsAvailable: numberValue(wallet.credits_available, 'credits_available'),
      planAllocation: numberValue(wallet.plan_credits_allocation, 'plan_credits_allocation'),
      cycleEndsAt: stringValue(wallet.cycle_ends_at, 'cycle_ends_at'),
      planTier: stringValue(wallet.plan_tier, 'plan_tier'),
    };
  }

  async listLedger(filters: CreditLedgerFilters = {}): Promise<CreditLedgerPage> {
    await this.ensureAuthenticatedOwner();
    const result = await this.rpc<unknown>('list_credit_ledger_v2', {
      p_cursor: filters.cursor ?? null,
      p_kind: filters.kind ?? null,
      p_limit: LEDGER_PAGE_SIZE,
    });
    return mapLedgerPage(result);
  }

  async getDailyUsage(): Promise<CreditDailyUsage[]> {
    await this.ensureAuthenticatedOwner();
    const rows = await this.rpc<unknown>('get_credit_daily_usage');
    if (!Array.isArray(rows))
      throw new Error('Credit daily usage RPC returned an invalid response');
    return rows.map((item, index) => {
      const row = asRecord(item);
      if (!row) throw new Error(`Credit daily usage RPC returned invalid item at index ${index}`);
      return {
        day: stringValue(row.day, 'day'),
        credits: numberValue(row.credits, 'credits'),
      };
    });
  }

  async getReceipts(requestIds: readonly string[]): Promise<CreditReceipt[]> {
    const requestIdsSorted = Array.from(new Set(requestIds.filter(Boolean))).sort();
    if (requestIdsSorted.length === 0) return [];
    if (requestIdsSorted.length > 100)
      throw new Error('At most 100 credit receipt request IDs are allowed');

    await this.ensureAuthenticatedOwner();
    const rows = await this.rpc<unknown>('list_credit_receipts', {
      p_request_ids: requestIdsSorted,
    });
    if (!Array.isArray(rows)) throw new Error('Credit receipt RPC returned an invalid response');

    const requested = new Set(requestIdsSorted);
    const receipts = new Map<string, CreditReceipt>();
    for (const [index, result] of rows.entries()) {
      const row = asRecord(result) as LedgerRpcRow | null;
      if (!row)
        throw new Error(`Credit receipt RPC returned invalid receipt item at index ${index}`);
      const requestId = stringValue(row.request_id, 'request_id');
      if (!requested.has(requestId)) {
        throw new Error('Credit receipt RPC returned an unexpected request_id');
      }

      const amount = numberValue(row.credits_delta, 'credits_delta');
      const item: CreditReceiptItem = {
        category: stringValue(row.category, 'category'),
        activityName: stringValue(row.activity_name, 'activity_name'),
        creditsCharged: Math.max(0, -amount),
        quantity: row.quantity == null ? 0 : numberValue(row.quantity, 'quantity'),
        occurredAt: stringValue(row.occurred_at, 'occurred_at'),
      };
      const existing = receipts.get(requestId);
      if (existing) {
        existing.creditsCharged += item.creditsCharged;
        existing.quantity += item.quantity;
        existing.items.push(item);
      } else {
        receipts.set(requestId, {
          requestId,
          creditsCharged: item.creditsCharged,
          quantity: item.quantity,
          activity: item.activityName,
          items: [item],
        });
      }
    }

    return Array.from(receipts.values()).sort((a, b) => a.requestId.localeCompare(b.requestId));
  }
}

export const creditService = new CreditService();
